import { Wallet } from "lucide-react";

import { ColocaMoneyTransit, type MoneyTransitEnd } from "@/shared/components/motion/ColocaMoneyTransit";
import useTranslations from "@/shared/lang/useTranslations";

import { MiniCard } from "./MiniCard";

export interface CardBalanceTransitProps {
  /** Loading from the card wallet, withdrawing back to it, or moving to another card. */
  mode: "load" | "withdraw" | "move";
  last4: string;
  /** The destination card of a move. */
  targetLast4?: string | null;
  /** The amount as it reads on screen ("$50.00", or "the whole balance"). */
  amountLabel: string;
  className?: string;
}

/** Dollars on their way between the card wallet and a card, or from one card to another. */
export function CardBalanceTransit({
  mode,
  last4,
  targetLast4,
  amountLabel,
  className,
}: CardBalanceTransitProps) {
  const { t } = useTranslations("cards");
  const wallet: MoneyTransitEnd = { icon: <Wallet className="size-5" />, label: t("balance_transit_wallet") };
  const card = (digits: string | null | undefined): MoneyTransitEnd => ({
    icon: <MiniCard />,
    label: t("cop_top_up_transit_card"),
    caption: digits ? <span className="font-mono">•••• {digits}</span> : undefined,
  });
  const [from, to] =
    mode === "load"
      ? [wallet, card(last4)]
      : mode === "withdraw"
        ? [card(last4), wallet]
        : [card(last4), card(targetLast4)];
  return (
    <ColocaMoneyTransit
      className={className}
      label={t("balance_transit_aria", { amount: amountLabel, from: from.label, to: to.label })}
      from={from}
      to={to}
      legs={[{ rail: { kind: "fiat" }, asset: { kind: "fiat", currency: "USD" } }]}
    />
  );
}
